import React, { createContext, useContext, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import GlobalPopup from './../Components/UC/GlobalPopup';

const GlobalPopupContext = createContext();

export const useGlobalPopup = () => {
  const context = useContext(GlobalPopupContext);
  if (!context) {
    throw new Error('useGlobalPopup must be used within a GlobalPopupProvider');
  }
  return context;
};

export const GlobalPopupProvider = ({ children }) => {
  const [popup, setPopup] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    content: '',
    options: [{ text: 'OK', action: null, type: 'default' }],
    showCloseButton: true,
    backdrop: true
  });

  // Create a special portal container for highest priority
  const [portalContainer, setPortalContainer] = useState(null);

  useEffect(() => {
    // Create a special div that will be rendered at the end of body
    let container = document.getElementById('global-popup-portal');
    if (!container) {
      container = document.createElement('div');
      container.id = 'global-popup-portal';
      container.style.position = 'fixed';
      container.style.top = '0';
      container.style.left = '0';
      container.style.width = '100%';
      container.style.height = '100%';
      container.style.pointerEvents = 'none';
      container.style.zIndex = '2147483647';
      document.body.appendChild(container);
    }
    setPortalContainer(container);

    return () => {
      // Cleanup portal container on unmount
      const existingContainer = document.getElementById('global-popup-portal');
      if (existingContainer && !document.querySelector('.global-popup-overlay')) {
        document.body.removeChild(existingContainer);
      }
    };
  }, []);

  const showPopup = ({
    type = 'info',
    title,
    content,
    options = [{ text: 'OK', action: null, type: 'default' }],
    showCloseButton = true,
    backdrop = true
  }) => {
    setPopup({
      isOpen: true,
      type,
      title,
      content,
      options,
      showCloseButton,
      backdrop
    });
  };

  const hidePopup = () => {
    setPopup(prev => ({ ...prev, isOpen: false }));
  };

  // Convenience methods for different types
  const showInfo = (title, content, options) => {
    showPopup({ type: 'info', title, content, options });
  };

  const showSuccess = (title, content, options) => {
    showPopup({ type: 'success', title, content, options });
  };

  const showWarning = (title, content, options) => {
    showPopup({ type: 'warning', title, content, options });
  };

  const showError = (title, content, options) => {
    showPopup({ type: 'error', title, content, options });
  };

  const showConfirm = (title, content, onConfirm, onCancel) => {
    showPopup({
      type: 'warning',
      title,
      content,
      options: [
        { text: 'Cancel', action: onCancel, type: 'secondary' },
        { text: 'Confirm', action: onConfirm, type: 'primary' }
      ],
      showCloseButton: false,
      backdrop: false
    });
  };

  const showDeleteConfirm = (title, content, onConfirm, onCancel) => {
    showPopup({
      type: 'error',
      title: title || 'Confirm Delete',
      content: content || 'Are you sure you want to delete this item? This action cannot be undone.',
      options: [
        { text: 'Cancel', action: onCancel, type: 'secondary' },
        { text: 'Delete', action: onConfirm, type: 'danger', icon: 'fa-trash' }
      ],
      showCloseButton: false,
      backdrop: false
    });
  };

  // Convenience methods for HTML content
  /**
   * Show an info popup with HTML content
   * @param {string} title - Popup title
   * @param {string} htmlContent - HTML string to render
   * @param {Array} options - Button options
   */
  const showHtmlInfo = (title, htmlContent, options) => {
    showPopup({ type: 'info', title, content: htmlContent, options });
  };

  /**
   * Show a success popup with HTML content
   * @param {string} title - Popup title
   * @param {string} htmlContent - HTML string to render
   * @param {Array} options - Button options
   */
  const showHtmlSuccess = (title, htmlContent, options) => {
    showPopup({ type: 'success', title, content: htmlContent, options });
  };

  /**
   * Show a warning popup with HTML content
   * @param {string} title - Popup title
   * @param {string} htmlContent - HTML string to render
   * @param {Array} options - Button options
   */
  const showHtmlWarning = (title, htmlContent, options) => {
    showPopup({ type: 'warning', title, content: htmlContent, options });
  };

  /**
   * Show an error popup with HTML content
   * @param {string} title - Popup title
   * @param {string} htmlContent - HTML string to render
   * @param {Array} options - Button options
   */
  const showHtmlError = (title, htmlContent, options) => {
    showPopup({ type: 'error', title, content: htmlContent, options });
  };

  return (
      <GlobalPopupContext.Provider value={{
        showPopup,
        hidePopup,
        showInfo,
        showSuccess,
        showWarning,
        showError,
        showConfirm,
        showDeleteConfirm,
        // HTML content convenience methods
        showHtmlInfo,
        showHtmlSuccess,
        showHtmlWarning,
        showHtmlError
      }}>
        {children}
        {/* Use Portal to render popup at custom container with highest z-index */}
        {portalContainer && createPortal(
          <div style={{ pointerEvents: popup.isOpen ? 'auto' : 'none' }}>
            <GlobalPopup
              isOpen={popup.isOpen}
              type={popup.type}
              title={popup.title}
              content={popup.content}
              options={popup.options}
              onClose={hidePopup}
              showCloseButton={popup.showCloseButton}
              backdrop={popup.backdrop}
            />
          </div>,
          portalContainer
        )}
      </GlobalPopupContext.Provider>
  );
};
